import React, { useEffect, useMemo, useState } from 'react';
import {
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  MoonIcon,
  UserPlusIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { analyticsAPI } from '../services/api';
import MetricHint from '../components/common/MetricHint';
import ExplainMetrics from '../components/common/ExplainMetrics';
import PlanUsagePanel from '../components/common/PlanUsagePanel';
import { useLicense } from '../hooks/useLicense';
import { useTenant } from '../contexts/TenantContext';
import InsightsTabs from '../components/common/InsightsTabs';

// Definitions surfaced by the Explain Metrics slide-over. Keep these in sync
// with the queries in impact_analytics.py — if you change how a KPI is
// computed, update the calculation string here too.
const METRIC_DEFINITIONS = [
  {
    name: 'Conversations this period',
    definition:
      'Chats started in the dates you picked. Each unique visitor conversation counts once, regardless of how many messages they sent.',
    calculation: 'COUNT(conversations WHERE tenant_id + created_at range)',
  },
  {
    name: 'After Hours %',
    definition:
      'Share of conversations that started outside your team’s working hours. This is the "your AI worked while you slept" number — it shows the value of always-on coverage.',
    calculation: 'after-hours conversations / total conversations',
    note:
      'Business hours are hardcoded to 09:00-18:00 IST for now. Ask us to change them if your team keeps different hours.',
  },
  {
    name: 'Leads Captured',
    definition:
      'Visitors whose contact details (name, email, or phone) the AI captured. Not every conversation becomes a lead — only qualified interest counts.',
    calculation: 'COUNT(leads WHERE tenant_id + created_at range)',
  },
  {
    name: 'Meetings Booked',
    definition:
      'Leads your AI qualified or your team converted — ready for a real sales conversation.',
    calculation: "COUNT(leads WHERE status IN ('qualified', 'converted'))",
    note:
      'Once we ship the dedicated calendar-booking flow, this will switch to counting actual booked slots. For now, "qualified" is the proxy.',
  },
  {
    name: 'Lead Funnel',
    definition:
      'Visitors → Engaged → Qualified → Meetings booked. Each stage shows conversion from the previous. Drop between stages tells you where to invest — better opener? better lead form? better handoff?',
    calculation: 'Widget opens → conversation starts → leads → qualified leads',
  },
  {
    name: 'AI Intent Themes',
    definition:
      'Recurring questions, objections, and knowledge gaps from real conversations, clustered weekly by our AI. Themes with lower resolution % show where your knowledge base has gaps to fill.',
    calculation: 'Sum message_count per theme from the weekly intent_themes rollup',
    note:
      'The resolved share is for all your conversations together, so it is shown once rather than on each theme.',
  },
  {
    name: 'Change vs last period (green/red chip)',
    definition:
      'Percent change vs the previous window of the same length. "no baseline" appears when you had zero conversations/leads in the prior period — normal when you are just starting.',
    calculation: '((current - previous) / previous) × 100',
  },
  {
    name: 'Team Performance',
    definition:
      'Per-owner breakdown of what your sales team did with the leads the AI captured. Shows leads assigned, deals won, deals lost, and the conversion rate for each team member. Unassigned leads sit at the top — those need triage.',
  },
  {
    name: 'Team: Deals Won / Lost',
    definition:
      'Deals Won = leads your team moved to status "converted". Deals Lost = leads moved to "lost". Both require manual status updates in the Leads page.',
    calculation: "COUNT(leads WHERE assigned_to = X AND status IN ('converted', 'lost'))",
  },
  {
    name: 'Team: Conversion %',
    definition:
      'Share of leads assigned to this owner that ended up as Deals Won. Higher = better sales close rate.',
    calculation: 'deals_won / leads_assigned',
  },
  {
    name: 'Team: Response Time (est.)',
    definition:
      'Rough estimate of how quickly a lead moves off "new" after being assigned. Uses updated_at - assigned_at for leads past the "new" stage.',
    note:
      'Estimate only — not true time-to-first-response. Precise tracking needs a lead_status_history audit trail, planned for a follow-up. Treat this as a rough directional signal, not a KPI to grade people on.',
  },
];

// Synthetic preview data for below-Scale tenants. This keeps the page useful
// as a product preview without calling Scale-only impact endpoints.
const DEMO_SUMMARY = {
  kpis: {
    conversations: { value: 128, delta_pct: 18 },
    after_hours_pct: { value: 42, delta_pct: 9 },
    leads_captured: { value: 36, delta_pct: 24 },
    meetings_booked: { value: 11, delta_pct: 15 },
  },
};

const DEMO_FUNNEL = {
  funnel: [
    { stage: 'Visitors', count: 420, rate_from_prev: null },
    { stage: 'Engaged conversations', count: 128, rate_from_prev: 30 },
    { stage: 'Qualified leads', count: 36, rate_from_prev: 28 },
    { stage: 'Meetings booked', count: 11, rate_from_prev: 31 },
  ],
};

const DEMO_TOPQ = {
  tenant_resolution_pct: 82,
  themes: [
    { theme: 'Pricing and payment options', message_count: 34, resolution_pct: 88 },
    { theme: 'Site visit or appointment requests', message_count: 26, resolution_pct: 92 },
    { theme: 'Location and availability', message_count: 21, resolution_pct: 76 },
    { theme: 'Brochure and project details', message_count: 17, resolution_pct: 68 },
  ],
};

const DEMO_TEAM = {
  team: [
    {
      owner_user_id: 'demo-priya',
      owner_name: 'Priya',
      leads_assigned: 14,
      deals_won: 5,
      deals_lost: 2,
      conversion_pct: 36,
      avg_response_hours_proxy: 2.1,
    },
    {
      owner_user_id: 'demo-rahul',
      owner_name: 'Rahul',
      leads_assigned: 10,
      deals_won: 3,
      deals_lost: 1,
      conversion_pct: 30,
      avg_response_hours_proxy: 3.4,
    },
    {
      owner_user_id: null,
      owner_name: 'Unassigned',
      unassigned: true,
      leads_assigned: 12,
      deals_won: 0,
      deals_lost: 0,
      conversion_pct: null,
      avg_response_hours_proxy: null,
    },
  ],
};

// Compact number: 1247 -> "1.2k", 89 -> "89"
const fmtNum = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const fmtPct = (n) => (n === null || n === undefined ? '—' : `${n}%`);

const DeltaChip = ({ delta }) => {
  if (delta === null || delta === undefined) {
    return <span className="text-xs text-gray-400">no baseline</span>;
  }
  const positive = delta >= 0;
  const Icon = positive ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  const cls = positive
    ? 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/40'
    : 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/40';
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" />
      {positive ? '+' : ''}{delta}% vs last period
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, delta, hint }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
      <Icon className="w-4 h-4" />
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      {hint && <MetricHint text={hint} />}
    </div>
    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{value}</div>
    <DeltaChip delta={delta} />
  </div>
);

// Lead funnel rendered as horizontal bars, width proportional to visitor count.
// Conversion % between stages sits to the right of each bar.
const FunnelBar = ({ stage, count, rateFromPrev, maxCount }) => {
  const widthPct = maxCount ? Math.max((count / maxCount) * 100, 4) : 4;
  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{stage}</span>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-semibold text-gray-900 dark:text-white">{fmtNum(count)}</span>
          {rateFromPrev !== null && rateFromPrev !== undefined && (
            <span className="ml-2 text-xs">{rateFromPrev}% of previous</span>
          )}
        </span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
};

const TopQuestionsRow = ({ theme, messageCount, resolutionPct }) => {
  const isGap = resolutionPct !== null && resolutionPct < 70;
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="min-w-0 flex-1 pr-3">
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{theme}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{fmtNum(messageCount)} messages</div>
      </div>
      {resolutionPct !== null && resolutionPct !== undefined && (
        <span
          className={`text-xs font-medium ${
            isGap
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-green-700 dark:text-green-400'
          }`}
        >
          {isGap ? '⚠ ' : '✓ '}
          {resolutionPct}% resolved
        </span>
      )}
    </div>
  );
};

export default function ImpactPage() {
  const { hasFeature, isLoading: licenseLoading } = useLicense();
  const { isSuperadmin } = useTenant();
  const previewMode = !licenseLoading && !hasFeature('ai_insights') && !isSuperadmin;
  const [period, setPeriod] = useState('30d');
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [topQ, setTopQ] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (licenseLoading) {
      setLoading(true);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);

    if (previewMode) {
      setSummary(DEMO_SUMMARY);
      setFunnel(DEMO_FUNNEL);
      setTopQ(DEMO_TOPQ);
      setTeam(DEMO_TEAM);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    Promise.all([
      analyticsAPI.getImpactSummary({ period }),
      analyticsAPI.getImpactFunnel({ period }),
      analyticsAPI.getImpactTopQuestions({ period, limit: 8 }),
      analyticsAPI.getImpactTeam({ period }),
    ])
      .then(([s, f, q, t]) => {
        if (cancelled) return;
        setSummary(s.data);
        setFunnel(f.data);
        setTopQ(q.data);
        setTeam(t.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || 'Failed to load impact data');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [period, previewMode, licenseLoading]);

  const funnelMax = useMemo(() => {
    if (!funnel?.funnel?.length) return 0;
    return Math.max(...funnel.funnel.map((s) => s.count || 0));
  }, [funnel]);

  const kpis = summary?.kpis || {};

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <InsightsTabs />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <SparklesIcon className="w-6 h-6 text-purple-500" />
            Your Serviti Impact
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            What your AI Marketing &amp; Sales Agent delivered — last {period.replace('d', ' days')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExplainMetrics items={METRIC_DEFINITIONS} />
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Scale preview banner - shown only in preview mode. Data shown below
          is synthetic sample data, not this tenant's real operating metrics. */}
      {previewMode && (
        <div className="mb-4 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 sm:p-5 shadow-md">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide opacity-80">Preview mode</p>
              <p className="mt-1 text-lg font-bold">AI Impact Dashboard is part of the AI Insights add-on (included in Scale)</p>
              <p className="mt-1 text-sm opacity-90 max-w-2xl">
                The numbers, funnel, team performance and intent themes below are sample data to show what your view will look like. Add AI Insights to see real impact metrics from your own leads, campaigns and conversations.
              </p>
            </div>
            <a
              href="mailto:sales@serviti.ai?subject=Upgrade%20to%20Scale%20-%20AI%20Impact%20Dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-indigo-700 font-semibold rounded-lg text-sm hover:bg-indigo-50 transition whitespace-nowrap"
            >
              Talk to sales
            </a>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm">
          {error}
        </div>
      )}

      {loading && !summary && (
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      )}

      {/* Hero KPI band */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <KpiCard
            icon={ChatBubbleLeftRightIcon}
            label="Conversations this period"
            value={fmtNum(kpis.conversations?.value)}
            delta={kpis.conversations?.delta_pct}
          />
          <KpiCard
            icon={MoonIcon}
            label="After Hours"
            value={fmtPct(kpis.after_hours_pct?.value)}
            delta={kpis.after_hours_pct?.delta_pct}
            hint="Conversations outside 09:00–18:00 IST. This is what your AI handles while your team is offline."
          />
          <KpiCard
            icon={UserPlusIcon}
            label="Leads Captured"
            value={fmtNum(kpis.leads_captured?.value)}
            delta={kpis.leads_captured?.delta_pct}
          />
          <KpiCard
            icon={CalendarDaysIcon}
            label="Meetings Booked"
            value={fmtNum(kpis.meetings_booked?.value)}
            delta={kpis.meetings_booked?.delta_pct}
            hint="Leads qualified or converted — ready for your sales team."
          />
        </div>
      )}

      {/* Two-column: Funnel + Top Questions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Lead Funnel */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-4">
            Lead Funnel
          </h2>
          {funnel?.funnel?.length ? (
            funnel.funnel.map((s) => (
              <FunnelBar
                key={s.stage}
                stage={s.stage}
                count={s.count}
                rateFromPrev={s.rate_from_prev}
                maxCount={funnelMax}
              />
            ))
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">No funnel data yet.</div>
          )}
        </div>

        {/* AI Intent Themes */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
              What visitors ask about
            </h2>
            {topQ?.tenant_resolution_pct !== null && topQ?.tenant_resolution_pct !== undefined && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {topQ.tenant_resolution_pct}% of AI-analysed conversations ended with a lead
              </span>
            )}
          </div>
          {topQ?.themes?.length ? (
            topQ.themes.map((t) => (
              <TopQuestionsRow
                key={t.theme}
                theme={t.theme}
                messageCount={t.message_count}
                resolutionPct={null}
              />
            ))
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              No themes clustered yet — themes are generated weekly from real conversations.
            </div>
          )}
        </div>
      </div>

      {/* Team Performance — WATI-style Operator Performance table */}
      <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide flex items-center gap-2">
            <UsersIcon className="w-4 h-4" />
            Team Performance
          </h2>
        </div>
        {team?.team?.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-3">Owner</th>
                  <th className="py-2 px-3 text-right">Leads</th>
                  <th className="py-2 px-3 text-right">Won</th>
                  <th className="py-2 px-3 text-right">Lost</th>
                  <th className="py-2 px-3 text-right">Conversion</th>
                  <th className="py-2 px-3 text-right whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      Response (est.)
                      <MetricHint
                        text="Rough estimate — avg time from lead assigned to first status change past 'new'. Not true first-response time. See Explain Metrics for details."
                        side="left"
                      />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-800 dark:text-gray-200">
                {team.team.map((row) => (
                  <tr
                    key={row.owner_user_id || 'unassigned'}
                    className={`border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                      row.unassigned ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''
                    }`}
                  >
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        {row.unassigned && (
                          <span className="inline-block w-2 h-2 rounded-full bg-amber-500" title="Needs triage" />
                        )}
                        <span className={row.unassigned ? 'italic text-amber-800 dark:text-amber-300' : 'font-medium'}>
                          {row.owner_name}
                        </span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{fmtNum(row.leads_assigned)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-green-700 dark:text-green-400">
                      {fmtNum(row.deals_won)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-red-700 dark:text-red-400">
                      {fmtNum(row.deals_lost)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">
                      {row.conversion_pct === null ? '—' : `${row.conversion_pct}%`}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {row.avg_response_hours_proxy === null ? '—' : `${row.avg_response_hours_proxy}h`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            No assigned leads in this period yet. Assign leads to team members from the Leads Management page to see per-owner metrics here.
          </div>
        )}
      </div>

      {/* Plan & Usage panel — glance-view of tier consumption. Full-page
          version at /plan-usage. Only shown for tenant users; superadmin
          without a tenant context gets an empty state from the endpoint. */}
      {!previewMode && (
        <div className="mt-4">
          <PlanUsagePanel compact />
        </div>
      )}
    </div>
  );
}
